/**
 * 智慧无限 func_tab 性能相关字段（与 keyboard_user_flash.c / 协议 sheet5 一致）
 */

/** 锁 Win：0 不锁 / 1 锁 */
export const ZHWX_FUNC_IDX_WIN_FLAG = 12;
/** 六键无冲：0 全键无冲 / 1 六键无冲 */
export const ZHWX_FUNC_IDX_KB_6KEY_MODE = 13;
/** 2.4G 关闭背光时间（16bit LE，秒）低字节索引 */
export const ZHWX_FUNC_IDX_D24G_OFF_LED_L = 15;
/** 2.4G 进入睡眠时间（16bit LE，秒）低字节索引 */
export const ZHWX_FUNC_IDX_D24G_SLEEP_L = 17;
/** USB 关闭背光时间（16bit LE，秒）低字节索引 */
export const ZHWX_FUNC_IDX_USB_OFF_LED_L = 23;

/** UI 休眠滑条最大分钟数 */
export const ZHWX_PERF_UI_MAX_MINUTES = 120;
/** UI 2.4G 休眠滑条最大分钟数 */
export const ZHWX_PERF_SLEEP_UI_MAX_MINUTES = 30;
/** UI 背光关闭滑条最小/最大分钟数（不支持永不关闭，0 秒会导致设备异常） */
export const ZHWX_PERF_BACKLIGHT_UI_MIN_MINUTES = 1;
export const ZHWX_PERF_BACKLIGHT_UI_MAX_MINUTES = 120;

/** 取消「永不休眠」时的默认分钟数（对齐固件默认约 10 分钟） */
export const ZHWX_PERF_UI_DEFAULT_MINUTES = 10;

export type ZhwxPerformanceConfig = {
  winLock: boolean;
  /** true = 全键无冲（kb_6key_mode=0） */
  globalNkro: boolean;
  usbOffLedMinutes: number;
  d24gOffLedMinutes: number;
  d24gSleepMinutes: number;
};

export function readZhwxFuncU16Le(func: Uint8Array, loIdx: number): number {
  return (func[loIdx] ?? 0) | ((func[loIdx + 1] ?? 0) << 8);
}

export function writeZhwxFuncU16Le(
  func: Uint8Array,
  loIdx: number,
  value: number
): void {
  const safe = Math.max(0, Math.min(0xffff, Math.round(value)));
  func[loIdx] = safe & 0xff;
  func[loIdx + 1] = (safe >> 8) & 0xff;
}

/** 协议秒 → UI 分钟；0 表示永不 */
export function zhwxSecondsToUiMinutes(
  sec: number,
  maxMinutes: number = ZHWX_PERF_UI_MAX_MINUTES
): number {
  const seconds = Math.max(0, Math.trunc(Number(sec) || 0));
  if (seconds === 0) return 0;
  return Math.max(1, Math.min(maxMinutes, Math.round(seconds / 60)));
}

/** UI 分钟 → 协议秒；0 表示永不 */
export function zhwxUiMinutesToSeconds(minutes: number): number {
  const mins = Math.max(0, Math.trunc(Number(minutes) || 0));
  if (mins === 0) return 0;
  return Math.min(0xffff, mins * 60);
}

/** 背光关闭：协议秒 → UI 分钟（0 秒按默认值展示，避免 UI 写入永不关闭） */
export function zhwxSecondsToUiBacklightMinutes(sec: number): number {
  const seconds = Math.max(0, Math.trunc(Number(sec) || 0));
  if (seconds === 0) return ZHWX_PERF_UI_DEFAULT_MINUTES;
  return Math.max(
    ZHWX_PERF_BACKLIGHT_UI_MIN_MINUTES,
    Math.min(ZHWX_PERF_BACKLIGHT_UI_MAX_MINUTES, Math.round(seconds / 60))
  );
}

/** 背光关闭：UI 分钟 → 协议秒（强制 1~120 分钟） */
export function zhwxUiBacklightMinutesToSeconds(minutes: number): number {
  const mins = Math.max(
    ZHWX_PERF_BACKLIGHT_UI_MIN_MINUTES,
    Math.min(
      ZHWX_PERF_BACKLIGHT_UI_MAX_MINUTES,
      Math.trunc(Number(minutes) || ZHWX_PERF_UI_DEFAULT_MINUTES)
    )
  );
  return Math.min(0xffff, mins * 60);
}

/** 从 func_tab 解析性能配置 */
export function parseZhwxPerformanceConfig(
  func: Uint8Array
): ZhwxPerformanceConfig {
  return {
    winLock: Number(func[ZHWX_FUNC_IDX_WIN_FLAG] ?? 0) === 1,
    globalNkro: Number(func[ZHWX_FUNC_IDX_KB_6KEY_MODE] ?? 0) === 0,
    usbOffLedMinutes: zhwxSecondsToUiBacklightMinutes(
      readZhwxFuncU16Le(func, ZHWX_FUNC_IDX_USB_OFF_LED_L)
    ),
    d24gOffLedMinutes: zhwxSecondsToUiBacklightMinutes(
      readZhwxFuncU16Le(func, ZHWX_FUNC_IDX_D24G_OFF_LED_L)
    ),
    d24gSleepMinutes: zhwxSecondsToUiMinutes(
      readZhwxFuncU16Le(func, ZHWX_FUNC_IDX_D24G_SLEEP_L),
      ZHWX_PERF_SLEEP_UI_MAX_MINUTES
    ),
  };
}

/** 将性能配置写入 func_tab buffer */
export function applyZhwxPerformanceConfig(
  func: Uint8Array,
  patch: Partial<ZhwxPerformanceConfig>
): void {
  if (patch.winLock != null) {
    func[ZHWX_FUNC_IDX_WIN_FLAG] = patch.winLock ? 1 : 0;
  }
  if (patch.globalNkro != null) {
    func[ZHWX_FUNC_IDX_KB_6KEY_MODE] = patch.globalNkro ? 0 : 1;
  }
  if (patch.usbOffLedMinutes != null) {
    writeZhwxFuncU16Le(
      func,
      ZHWX_FUNC_IDX_USB_OFF_LED_L,
      zhwxUiBacklightMinutesToSeconds(patch.usbOffLedMinutes)
    );
  }
  if (patch.d24gOffLedMinutes != null) {
    writeZhwxFuncU16Le(
      func,
      ZHWX_FUNC_IDX_D24G_OFF_LED_L,
      zhwxUiBacklightMinutesToSeconds(patch.d24gOffLedMinutes)
    );
  }
  if (patch.d24gSleepMinutes != null) {
    writeZhwxFuncU16Le(
      func,
      ZHWX_FUNC_IDX_D24G_SLEEP_L,
      zhwxUiMinutesToSeconds(patch.d24gSleepMinutes)
    );
  }
}
